/** Visual editor для site_content JSONB.
 *
 * Загружает raw структуру через GET /admin/v1/site-content, рендерит
 * рекурсивно: вместо JSON-tree — табы по разделам (Brand / Hero /
 * Navigation / About / Delivery / Policy / Footer), внутри — поля
 * с 3 textarea на каждое i18n-значение (ru / uz / en).
 *
 * Save → PUT весь объект обратно. Backend service replace полностью.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { CollapsibleSection } from "@/components/common/CollapsibleSection";
import { ImageUploader } from "@/components/common/ImageUploader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { api } from "@/lib/api";

const LANGS = ["ru", "uz", "en"] as const;
type Lang = (typeof LANGS)[number];

type I18nValue = { ru?: string; uz?: string; en?: string };

interface SiteContentRaw {
  brand?: { name?: I18nValue; tagline?: I18nValue };
  hero?: {
    title?: I18nValue;
    subtitle?: I18nValue;
    cta_text?: I18nValue;
    cta_path?: string;
  };
  navigation?: { key: string; path: string; label: I18nValue }[];
  about?: {
    title?: I18nValue;
    paragraphs?: I18nValue[];
    bullets_title?: I18nValue;
    bullets?: I18nValue[];
  };
  delivery_page?: {
    title?: I18nValue;
    delivery_section_title?: I18nValue;
    payment_section_title?: I18nValue;
    payment_methods?: { name: I18nValue; description: I18nValue }[];
    payment_footer?: I18nValue;
  };
  policy_page?: {
    title?: I18nValue;
    intro?: I18nValue;
    sections?: {
      title?: I18nValue;
      content?: I18nValue;
      items?: I18nValue[];
    }[];
  };
  footer?: {
    address_line?: I18nValue;
    rights_template?: I18nValue;
    // Phase C: новые редактируемые поля футера
    newsletter_heading?: I18nValue;
    newsletter_consent?: I18nValue;
    apps_qr_url?: I18nValue;
    apps_caption_top?: I18nValue;
    apps_caption_bottom?: I18nValue;
    legal_intro?: I18nValue;
  };
}

export function SiteContentPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SiteContentRaw | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-site-content"],
    queryFn: async () => {
      const { data } = await api.get<SiteContentRaw>("/site-config");
      return data;
    },
  });

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("Nothing to save");
      const { data } = await api.put<SiteContentRaw>("/site-config", draft);
      return data;
    },
    onSuccess: () => {
      toast.success("Контент сохранён. Фронты обновятся в течение 5 минут (ISR cache).");
      queryClient.invalidateQueries({ queryKey: ["admin-site-content"] });
    },
    onError: (e: any) => toast.error(`Ошибка: ${e?.message ?? "unknown"}`),
  });

  if (isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
        Не удалось загрузить site-content. Проверь подключение или роль.
      </div>
    );
  }

  // ─── helpers для иммутабельного обновления вложенных полей ────────
  const firstNonEmpty = (v?: I18nValue) =>
    (v?.ru || v?.uz || v?.en || "").trim();

  const patch = (updater: (d: SiteContentRaw) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as SiteContentRaw;
      updater(next);
      return next;
    });
  };

  return (
    <div className="space-y-4 pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading">Контент сайта</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Один источник для web, TMA и mobile. Тексты обновятся за 5 мин.
          </p>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Сохранить
        </Button>
      </div>

      <Tabs defaultValue="brand" className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="brand">Бренд</TabsTrigger>
          <TabsTrigger value="hero">Hero</TabsTrigger>
          <TabsTrigger value="navigation">Меню</TabsTrigger>
          <TabsTrigger value="about">О компании</TabsTrigger>
          <TabsTrigger value="delivery">Доставка</TabsTrigger>
          <TabsTrigger value="policy">Политика</TabsTrigger>
          <TabsTrigger value="footer">Футер</TabsTrigger>
        </TabsList>

        {/* Brand */}
        <TabsContent value="brand" className="space-y-4">
          <I18nField
            label="Название бренда"
            value={draft.brand?.name ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.brand = { ...d.brand, name: v };
              })
            }
          />
          <I18nField
            label="Слоган"
            value={draft.brand?.tagline ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.brand = { ...d.brand, tagline: v };
              })
            }
          />
        </TabsContent>

        {/* Hero */}
        <TabsContent value="hero" className="space-y-4">
          <I18nField
            label="Заголовок"
            value={draft.hero?.title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.hero = { ...d.hero, title: v };
              })
            }
          />
          <I18nField
            label="Подзаголовок"
            value={draft.hero?.subtitle ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.hero = { ...d.hero, subtitle: v };
              })
            }
            multiline
          />
          <I18nField
            label="Текст кнопки"
            value={draft.hero?.cta_text ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.hero = { ...d.hero, cta_text: v };
              })
            }
          />
          <FieldRow label="Путь кнопки">
            <Input
              value={draft.hero?.cta_path ?? ""}
              onChange={(e) =>
                patch((d) => {
                  d.hero = { ...d.hero, cta_path: e.target.value };
                })
              }
              placeholder="/catalog"
            />
          </FieldRow>
        </TabsContent>

        {/* Navigation */}
        <TabsContent value="navigation" className="space-y-3">
          {(draft.navigation ?? []).map((item, i) => (
            <CollapsibleSection
              key={i}
              title={firstNonEmpty(item.label) || item.key || `Пункт #${i + 1}`}
              subtitle={item.path}
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    patch((d) => {
                      d.navigation = (d.navigation ?? []).filter(
                        (_, j) => j !== i,
                      );
                    });
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              }
            >
              <FieldRow label="key (внутр. ID)">
                <Input
                  value={item.key}
                  onChange={(e) =>
                    patch((d) => {
                      const it = d.navigation?.[i];
                      if (it) it.key = e.target.value;
                    })
                  }
                />
              </FieldRow>
              <FieldRow label="Путь">
                <Input
                  value={item.path}
                  onChange={(e) =>
                    patch((d) => {
                      const it = d.navigation?.[i];
                      if (it) it.path = e.target.value;
                    })
                  }
                />
              </FieldRow>
              <I18nField
                label="Название в меню"
                value={item.label}
                onChange={(v) =>
                  patch((d) => {
                    const it = d.navigation?.[i];
                    if (it) it.label = v;
                  })
                }
              />
            </CollapsibleSection>
          ))}
          <Button
            variant="outline"
            onClick={() =>
              patch((d) => {
                d.navigation = [
                  ...(d.navigation ?? []),
                  { key: "", path: "/", label: { ru: "", uz: "", en: "" } },
                ];
              })
            }
          >
            <Plus className="w-4 h-4" />
            Добавить пункт меню
          </Button>
        </TabsContent>

        {/* About */}
        <TabsContent value="about" className="space-y-4">
          <I18nField
            label="Заголовок страницы"
            value={draft.about?.title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.about = { ...d.about, title: v };
              })
            }
          />
          <ListEditor
            label="Параграфы"
            items={draft.about?.paragraphs ?? []}
            onChange={(items) =>
              patch((d) => {
                d.about = { ...d.about, paragraphs: items };
              })
            }
            multiline
          />
          <I18nField
            label="Заголовок «Почему мы»"
            value={draft.about?.bullets_title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.about = { ...d.about, bullets_title: v };
              })
            }
          />
          <ListEditor
            label="Пункты"
            items={draft.about?.bullets ?? []}
            onChange={(items) =>
              patch((d) => {
                d.about = { ...d.about, bullets: items };
              })
            }
          />
        </TabsContent>

        {/* Delivery */}
        <TabsContent value="delivery" className="space-y-4">
          <I18nField
            label="Заголовок страницы"
            value={draft.delivery_page?.title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.delivery_page = { ...d.delivery_page, title: v };
              })
            }
          />
          <I18nField
            label="Подзаголовок секции доставки"
            value={draft.delivery_page?.delivery_section_title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.delivery_page = {
                  ...d.delivery_page,
                  delivery_section_title: v,
                };
              })
            }
          />
          <I18nField
            label="Подзаголовок секции оплаты"
            value={draft.delivery_page?.payment_section_title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.delivery_page = {
                  ...d.delivery_page,
                  payment_section_title: v,
                };
              })
            }
          />
          <div className="space-y-2">
            <div className="text-sm font-medium mb-2">Способы оплаты</div>
            {(draft.delivery_page?.payment_methods ?? []).map((m, i) => (
              <CollapsibleSection
                key={i}
                title={firstNonEmpty(m.name) || `Метод #${i + 1}`}
                subtitle={firstNonEmpty(m.description)}
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      patch((d) => {
                        if (d.delivery_page?.payment_methods) {
                          d.delivery_page.payment_methods =
                            d.delivery_page.payment_methods.filter(
                              (_, j) => j !== i,
                            );
                        }
                      });
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                }
              >
                <I18nField
                  label="Название"
                  value={m.name}
                  onChange={(v) =>
                    patch((d) => {
                      const m = d.delivery_page?.payment_methods?.[i];
                      if (m) m.name = v;
                    })
                  }
                />
                <I18nField
                  label="Описание"
                  value={m.description}
                  onChange={(v) =>
                    patch((d) => {
                      const m = d.delivery_page?.payment_methods?.[i];
                      if (m) m.description = v;
                    })
                  }
                  multiline
                />
              </CollapsibleSection>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                patch((d) => {
                  d.delivery_page = {
                    ...d.delivery_page,
                    payment_methods: [
                      ...(d.delivery_page?.payment_methods ?? []),
                      {
                        name: { ru: "", uz: "", en: "" },
                        description: { ru: "", uz: "", en: "" },
                      },
                    ],
                  };
                })
              }
            >
              <Plus className="w-4 h-4" />
              Добавить способ
            </Button>
          </div>
          <I18nField
            label="Примечание внизу секции оплаты"
            value={draft.delivery_page?.payment_footer ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.delivery_page = { ...d.delivery_page, payment_footer: v };
              })
            }
            multiline
          />
        </TabsContent>

        {/* Policy */}
        <TabsContent value="policy" className="space-y-4">
          <I18nField
            label="Заголовок страницы"
            value={draft.policy_page?.title ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.policy_page = { ...d.policy_page, title: v };
              })
            }
          />
          <I18nField
            label="Вступление"
            value={draft.policy_page?.intro ?? {}}
            onChange={(v) =>
              patch((d) => {
                d.policy_page = { ...d.policy_page, intro: v };
              })
            }
            multiline
          />
          <div className="space-y-2">
            <div className="text-sm font-medium mb-2">Секции политики</div>
            {(draft.policy_page?.sections ?? []).map((s, i) => (
              <CollapsibleSection
                key={i}
                title={firstNonEmpty(s.title) || `Секция #${i + 1}`}
                subtitle={firstNonEmpty(s.content)}
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      patch((d) => {
                        if (d.policy_page?.sections) {
                          d.policy_page.sections =
                            d.policy_page.sections.filter((_, j) => j !== i);
                        }
                      });
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                }
              >
                <I18nField
                  label="Заголовок"
                  value={s.title ?? {}}
                  onChange={(v) =>
                    patch((d) => {
                      const s = d.policy_page?.sections?.[i];
                      if (s) s.title = v;
                    })
                  }
                />
                <I18nField
                  label="Содержание (один параграф)"
                  value={s.content ?? {}}
                  onChange={(v) =>
                    patch((d) => {
                      const s = d.policy_page?.sections?.[i];
                      if (s) s.content = v;
                    })
                  }
                  multiline
                />
                <ListEditor
                  label="Пункты списка (опц.)"
                  items={s.items ?? []}
                  onChange={(items) =>
                    patch((d) => {
                      const s = d.policy_page?.sections?.[i];
                      if (s) s.items = items;
                    })
                  }
                />
              </CollapsibleSection>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                patch((d) => {
                  d.policy_page = {
                    ...d.policy_page,
                    sections: [
                      ...(d.policy_page?.sections ?? []),
                      {
                        title: { ru: "", uz: "", en: "" },
                        content: { ru: "", uz: "", en: "" },
                      },
                    ],
                  };
                })
              }
            >
              <Plus className="w-4 h-4" />
              Добавить секцию
            </Button>
          </div>
        </TabsContent>

        {/* Footer */}
        <TabsContent value="footer" className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Контакты и копирайт
            </h3>
            <I18nField
              label="Адрес"
              value={draft.footer?.address_line ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, address_line: v };
                })
              }
            />
            <I18nField
              label="Шаблон копирайта"
              value={draft.footer?.rights_template ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, rights_template: v };
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              В шаблоне можно использовать <code>{"{year}"}</code> — будет
              подставлен текущий год.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Newsletter (плашка подписки сверху футера)
            </h3>
            <I18nField
              label="Заголовок"
              value={draft.footer?.newsletter_heading ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, newsletter_heading: v };
                })
              }
            />
            <I18nField
              label="Подзаголовок / согласие"
              value={draft.footer?.newsletter_consent ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, newsletter_consent: v };
                })
              }
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Приложение (QR + подписи)
            </h3>
            <div className="space-y-2">
              <Label className="text-xs">Картинка QR-кода</Label>
              <div className="max-w-xs">
                <ImageUploader
                  value={draft.footer?.apps_qr_url?.ru ?? ""}
                  hint="Загрузить QR-код (PNG/SVG)"
                  previewClassName="h-40"
                  onChange={(url) =>
                    patch((d) => {
                      // Картинка одна на все языки — пишем во все 3
                      d.footer = {
                        ...d.footer,
                        apps_qr_url: { ru: url, uz: url, en: url },
                      };
                    })
                  }
                  onRemove={() =>
                    patch((d) => {
                      d.footer = {
                        ...d.footer,
                        apps_qr_url: { ru: "", uz: "", en: "" },
                      };
                    })
                  }
                />
              </div>
              <p className="text-xs text-muted-foreground">
                PNG/SVG, до 10 MB. Можно перетащить файл сюда. Если пусто —
                блок QR в футере скрывается. Кнопки сторов настраиваются в{" "}
                <code>/configuration/navigation → Footer — приложения</code>.
              </p>
            </div>
            <I18nField
              label="Подпись сверху"
              value={draft.footer?.apps_caption_top ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, apps_caption_top: v };
                })
              }
            />
            <I18nField
              label="Подпись снизу"
              value={draft.footer?.apps_caption_bottom ?? {}}
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, apps_caption_bottom: v };
                })
              }
            />
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Legal (текст внизу футера)
            </h3>
            <I18nField
              label="Вступительный текст"
              value={draft.footer?.legal_intro ?? {}}
              multiline
              onChange={(v) =>
                patch((d) => {
                  d.footer = { ...d.footer, legal_intro: v };
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              Ссылки внутри (Политика, Соглашение) — в{" "}
              <code>/configuration/navigation → Footer — legal</code>.
            </p>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function I18nField({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string;
  value: I18nValue;
  onChange: (v: I18nValue) => void;
  multiline?: boolean;
}) {
  return (
    <FieldRow label={label}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {LANGS.map((lang) => (
          <LangField
            key={lang}
            lang={lang}
            value={value[lang] ?? ""}
            multiline={multiline}
            onChange={(v) => onChange({ ...value, [lang]: v })}
          />
        ))}
      </div>
    </FieldRow>
  );
}

function LangField({
  lang,
  value,
  multiline,
  onChange,
}: {
  lang: Lang;
  value: string;
  multiline?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
        {lang.toUpperCase()}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-sm font-medium mb-1">{label}</div>
      {children}
    </div>
  );
}

function ListEditor({
  label,
  items,
  onChange,
  multiline,
}: {
  label: string;
  items: I18nValue[];
  onChange: (next: I18nValue[]) => void;
  multiline?: boolean;
}) {
  return (
    <div>
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="rounded-md border bg-card p-3 space-y-2">
            <div className="flex items-center justify-between">
              <strong className="text-xs text-muted-foreground">
                #{i + 1}
              </strong>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  onChange(items.filter((_, j) => j !== i))
                }
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {LANGS.map((lang) => (
                <LangField
                  key={lang}
                  lang={lang}
                  value={it[lang] ?? ""}
                  multiline={multiline}
                  onChange={(v) => {
                    const next = [...items];
                    next[i] = { ...it, [lang]: v };
                    onChange(next);
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-2"
        onClick={() =>
          onChange([...items, { ru: "", uz: "", en: "" }])
        }
      >
        <Plus className="w-4 h-4" />
        Добавить
      </Button>
    </div>
  );
}
